// WormWatcher.jsx
import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import Box from "@mui/material/Box";
import { red } from "@mui/material/colors";

export default function WormWatcher({
  size = 180,
  bottom = 16,
  left = 16,
  zIndex = 1300,
  hoverRadiusPx = 140,
  jumpPower = 16,
  jumpDurationMs = 5000,
  loading = false,
  loadingSpeed = 1,
  loadingRadiusFactor = 0.7,
}) {
  const RED = "#FF3B30";
  const ORANGE = "#FF6A00";
  const AMBER = "#C07A00";
  const STROKE = "#ffa000";

  const VIEW_W = 120;
  const VIEW_H = 120;

  // --- УСМІШКА: зменшено ×1.5, центр лишається ---
  const MOUTH_Y = 82;
  const MOUTH_L0 = 36;
  const MOUTH_R0 = 84;
  const MOUTH_ARC0 = 12;
  const MOUTH_SW0 = 12;
  const MID_X = (MOUTH_L0 + MOUTH_R0) / 2;         // 60

  const MOUTH_SCALE = 1 / 1.5;                      // ≈0.6667
  const half0 = (MOUTH_R0 - MOUTH_L0) / 2;          // 24
  const halfNew = half0 * MOUTH_SCALE;              // 16
  const MOUTH_L = MID_X - halfNew;                  // 44
  const MOUTH_R = MID_X + halfNew;                  // 76
  const MOUTH_ARC = MOUTH_ARC0 * MOUTH_SCALE;       // 8
  const MOUTH_SW = Math.max(1, MOUTH_SW0 * MOUTH_SCALE); // 8

  const mouthD = `M ${MOUTH_L} ${MOUTH_Y} Q ${MID_X} ${MOUTH_Y + MOUTH_ARC} ${MOUTH_R} ${MOUTH_Y}`;

  // --- ОЧІ: 1px контур, 1.5× менші, 1.5× ближче одне до одного, 2× ближче до усмішки ---
  const EYE_STROKE_W = 1;
  const SCALE = 1 / 2;
  const EYE_R = 10 * SCALE;
  const PUPIL_R = 4.4 * SCALE;

  const ORIG_E1X = 48;
  const ORIG_E2X = 72;
  const ORIG_EY = 40;

  const midX = (ORIG_E1X + ORIG_E2X) / 2;          // 60
  const newHalfGap = ((ORIG_E2X - ORIG_E1X) / 1.5) / 2; // 12
  const EYE1_X = midX - newHalfGap;                // 48
  const EYE2_X = midX + newHalfGap;                // 72
  const newEyeY = MOUTH_Y - ((MOUTH_Y - ORIG_EY) / 2);  // 60

  const EYE1 = { x: EYE1_X, y: newEyeY };
  const EYE2 = { x: EYE2_X, y: newEyeY };

  const MAX = EYE_R - PUPIL_R - 1.2;

  // stabilize constant objects so they can be safely used in effects
  const eye1Ref = useRef(EYE1);
  const eye2Ref = useRef(EYE2);
  const hoverCenterRef = useRef({ x: MID_X, y: (newEyeY + MOUTH_Y) / 2 });
  const maxRef = useRef(MAX);

  const ref = useRef(null);
  const targetP1 = useRef({ dx: 0, dy: 0 });
  const targetP2 = useRef({ dx: 0, dy: 0 });
  const [p1, setP1] = useState({ dx: 0, dy: 0 });
  const [p2, setP2] = useState({ dx: 0, dy: 0 });
  const [blinkScale, setBlinkScale] = useState(1);

  // підстрибування
  const [bounceY, setBounceY] = useState(0);
  const vyRef = useRef(0);
  const jumpingUntilRef = useRef(0);
  const wasInsideRef = useRef(false);

  // loading-анімація
  const thetaRef = useRef(0);
  const lastTsRef = useRef(0);

  // HOVER_CENTER constant removed; use hoverCenterRef instead

  // курсор (вимкнений у loading)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = ref.current;
    if (!el) return () => {};

    const onMove = (e) => {
      if (loading) return;

      const rect = el.getBoundingClientRect();
      const sx = rect.width / VIEW_W;
      const sy = rect.height / VIEW_H;

      const calc = (eye) => {
        const cx = rect.left + eye.x * sx;
        const cy = rect.top + eye.y * sy;
        const dx = (e.clientX - cx) / sx;
        const dy = (e.clientY - cy) / sy;
        const d = Math.hypot(dx, dy) || 1;
        const k = Math.min(maxRef.current / d, 1);
        return { dx: dx * k, dy: dy * k };
      };

      targetP1.current = calc(eye1Ref.current);
      targetP2.current = calc(eye2Ref.current);

      const hx = rect.left + hoverCenterRef.current.x * sx;
      const hy = rect.top + hoverCenterRef.current.y * sy;
      const inside = Math.hypot(e.clientX - hx, e.clientY - hy) <= hoverRadiusPx;

      if (inside && !wasInsideRef.current) {
        jumpingUntilRef.current = Date.now() + jumpDurationMs;
        vyRef.current = -jumpPower;
      }
      wasInsideRef.current = inside;
    };

    const reset = () => {
      targetP1.current = { dx: 0, dy: 0 };
      targetP2.current = { dx: 0, dy: 0 };
      wasInsideRef.current = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", reset);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", reset);
      window.removeEventListener("blur", reset);
    };
  }, [hoverRadiusPx, jumpPower, jumpDurationMs, loading]);

  // RAF: очі (інерція/коло) + стрибки
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let rafId;
    const alpha = 0.12;

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      if (loading) {
        const r = maxRef.current * loadingRadiusFactor;
        thetaRef.current += (Math.PI * 2) * loadingSpeed * dt;
        const t = thetaRef.current;
        setP1({ dx: r * Math.cos(t),            dy: r * Math.sin(t) });
        setP2({ dx: r * Math.cos(t + Math.PI/4), dy: r * Math.sin(t + Math.PI/4) });
        setBounceY(0);
        vyRef.current = 0;
      } else {
        setP1((prev) => ({
          dx: prev.dx + (targetP1.current.dx - prev.dx) * alpha,
          dy: prev.dy + (targetP1.current.dy - prev.dy) * alpha,
        }));
        setP2((prev) => ({
          dx: prev.dx + (targetP2.current.dx - prev.dx) * alpha,
          dy: prev.dy + (targetP2.current.dy - prev.dy) * alpha,
        }));

        const now = Date.now();
        const jumpingActive = now < jumpingUntilRef.current;
        if (jumpingActive || Math.abs(bounceY) > 0.0001 || Math.abs(vyRef.current) > 0.0001) {
          let y = bounceY + vyRef.current;
          vyRef.current += 0.95;
          if (y >= 0) {
            y = 0;
            vyRef.current = jumpingActive ? -jumpPower : 0;
          }
          setBounceY(y);
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [bounceY, jumpPower, loading, loadingSpeed, loadingRadiusFactor]);

  // моргання
  useEffect(() => {
    let t1;
    let t2;
    const blinkOnce = () => {
      setBlinkScale(0.12);
      t2 = setTimeout(() => setBlinkScale(1), 140);
      t1 = setTimeout(blinkOnce, 2200 + Math.random() * 3200);
    };
    t1 = setTimeout(blinkOnce, 900 + Math.random() * 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const eyeTransform = (cx, cy) =>
    `translate(${cx} ${cy}) scale(1 ${blinkScale}) translate(${-cx} ${-cy})`;

  const rotate = loading ? 0 : Math.max(-6, Math.min(6, -vyRef.current * 0.7));

  return (
    <Box
      ref={ref}
      aria-hidden
      sx={{
        position: "fixed",
        bottom,
        left,
        width: size,
        height: (size * VIEW_H) / VIEW_W,
        pointerEvents: "none",
        zIndex,
        userSelect: "none",
        transform: `translateY(${bounceY}px) rotate(${rotate}deg)`,
        transition: "transform 120ms ease",
        filter: "drop-shadow(0 6px 16px rgba(255, 0, 0, 0.1))",
      }}
    >
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} width="100%" height="100%">
        {/* посмішка (зменшена ×1.5) */}
        <path d={mouthD} stroke={RED} strokeWidth={MOUTH_SW} fill="none" strokeLinecap="round" />
        <circle cx={MOUTH_L} cy={MOUTH_Y} r={MOUTH_SW / 2} fill={ORANGE} />
        <circle cx={MOUTH_R} cy={MOUTH_Y} r={MOUTH_SW / 2} fill={AMBER} />

        {/* очі з обводом 1px */}
        <g transform={eyeTransform(EYE1.x, EYE1.y)} style={{ transition: "transform 100ms ease" }}>
          <circle cx={EYE1.x} cy={EYE1.y} r={EYE_R} fill="#fff" stroke={STROKE} strokeWidth={EYE_STROKE_W} />
          <circle cx={EYE1.x + p1.dx} cy={EYE1.y + p1.dy} r={PUPIL_R} fill={red} />
        </g>
        <g transform={eyeTransform(EYE2.x, EYE2.y)} style={{ transition: "transform 100ms ease" }}>
          <circle cx={EYE2.x} cy={EYE2.y} r={EYE_R} fill="#fff" stroke={STROKE} strokeWidth={EYE_STROKE_W} />
          <circle cx={EYE2.x + p2.dx} cy={EYE2.y + p2.dy} r={PUPIL_R} fill={red} />
        </g>
      </svg>
    </Box>
  );
}

WormWatcher.propTypes = {
  size: PropTypes.number,
  bottom: PropTypes.number,
  left: PropTypes.number,
  zIndex: PropTypes.number,
  hoverRadiusPx: PropTypes.number,
  jumpPower: PropTypes.number,
  jumpDurationMs: PropTypes.number,
  loading: PropTypes.bool,
  loadingSpeed: PropTypes.number,
  loadingRadiusFactor: PropTypes.number,
};
